import React, { useEffect, useState, useCallback } from "react";
import { Box, Flex, Checkbox, Label } from "theme-ui";
import { useNavigate } from "react-router-dom";
import { getExpiringItems, batchDecline, batchRegenerate } from "api/QuoteExpiration";
import { themevals } from "theme/themevals";
import BasicButton from "components/forms/BasicButton";
import DeclineReasonModal from "components/DeclineReasonModal";
import ExpiringItemsModal from "components/ExpiringItemsModal";
import { useToast } from "components/toast";

const COMPONENT_HEIGHT = 500;
const MAX_DISPLAY = 10;

interface ExpiringItem {
  id: string;
  type: "quote" | "rfq";
  sequence: number;
  projectName?: string;
  status: number;
  expiresAt: string;
  daysUntilExpiry: number;
  clientName?: string;
  total?: number;
}

const formatDate = (dateStr: string): string => {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const formatCurrency = (value: number): string => {
  return "$" + value.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const getUrgency = (daysUntilExpiry: number): "critical" | "warning" | "normal" => {
  if (daysUntilExpiry <= 0) return "critical";
  if (daysUntilExpiry <= 3) return "critical";
  if (daysUntilExpiry <= 7) return "warning";
  return "normal";
};

const ExpiringItemRow: React.FC<{
  item: ExpiringItem;
  onReview: (item: ExpiringItem) => void;
  selected: boolean;
  onToggle: (id: string, type: string) => void;
}> = ({ item, onReview, selected, onToggle }) => {
  const urgency = getUrgency(item.daysUntilExpiry);
  const isCritical = urgency === "critical";
  const isWarning = urgency === "warning";
  const prefix = item.type === "quote" ? "GMQ" : "GMR";
  const label = item.type === "quote" ? "Quote" : "RFQ";
  const daysText = item.daysUntilExpiry <= 0
    ? "Expired"
    : item.daysUntilExpiry === 1
      ? "1 day left"
      : item.daysUntilExpiry + " days left";

  return (
    <Flex
      sx={{
        alignItems: "center",
        gap: "12px",
        padding: "12px 16px 12px 12px",
        position: "relative",
        border: "1px solid " + themevals.colors.inputOutline,
        borderRadius: "6px",
        backgroundColor: selected ? themevals.colors.info + "08" : "transparent",
        transition: "background-color 0.15s ease",
      }}
    >
      {/* Priority indicator bar */}
      <Box
        sx={{
          position: "absolute",
          left: "0px",
          top: "0px",
          bottom: "0px",
          width: "4px",
          borderRadius: "2px",
          backgroundColor: isCritical ? themevals.colors.danger : isWarning ? themevals.colors.warning : "transparent",
          borderTopLeftRadius: "6px",
          borderBottomLeftRadius: "6px",
        }}
      />

      {/* Checkbox */}
      <Label sx={{ display: "flex", alignItems: "center", cursor: "pointer", flexShrink: 0, margin: 0, padding: 0, minWidth: "18px" }}>
        <Checkbox
          checked={selected}
          onChange={() => onToggle(item.id, item.type)}
          sx={{ cursor: "pointer" }}
        />
      </Label>

      {/* Type badge */}
      <Box
        sx={{
          fontFamily: "Poppins, sans-serif",
          fontSize: "11px",
          fontWeight: 600,
          color: item.type === "quote" ? themevals.colors.info : themevals.colors.primary,
          backgroundColor: item.type === "quote" ? themevals.colors.info + "15" : themevals.colors.primary + "15",
          padding: "2px 8px",
          borderRadius: "4px",
          flexShrink: 0,
        }}
      >
        {label}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Flex sx={{ alignItems: "center", gap: "8px", marginBottom: "2px" }}>
          <Box
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "13px",
              fontWeight: 600,
              color: themevals.colors.black,
              lineHeight: 1.4,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {prefix}-{String(item.sequence).padStart(5, "0")}
            {item.projectName ? " - " + item.projectName : ""}
          </Box>
          {item.total != null && item.total > 0 && (
            <Box
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "12px",
                fontWeight: 600,
                color: themevals.colors.dkgrey,
                flexShrink: 0,
              }}
            >
              {formatCurrency(item.total)}
            </Box>
          )}
        </Flex>
        <Flex sx={{ gap: "12px", alignItems: "center" }}>
          {item.clientName && (
            <Box
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "12px",
                color: themevals.colors.dkgrey,
                lineHeight: 1.4,
              }}
            >
              {item.clientName}
            </Box>
          )}
          <Box
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "12px",
              color: themevals.colors.dkgrey,
              lineHeight: 1.4,
            }}
          >
            Expires {formatDate(item.expiresAt)}
          </Box>
          <Box
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "11px",
              fontWeight: 600,
              color: isCritical ? themevals.colors.danger : isWarning ? themevals.colors.warning : themevals.colors.dkgrey,
            }}
          >
            {daysText}
          </Box>
        </Flex>
      </Box>

      {/* Review button */}
      <BasicButton
        scheme="secondary"
        outline={true}
        onClick={() => onReview(item)}
        styles={{ flexShrink: "0" }}
      >
        Review
      </BasicButton>
    </Flex>
  );
};

const ExpiringItems: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [items, setItems] = useState<ExpiringItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBatchDecline, setShowBatchDecline] = useState(false);
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const [regenSubmitting, setRegenSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const fetchData = useCallback(async () => {
    const data = await getExpiringItems();
    const now = new Date();
    const combined: ExpiringItem[] = [];

    if (data.quotes && Array.isArray(data.quotes)) {
      for (const q of data.quotes) {
        const expiresAt = new Date(q.expiresAt);
        const daysUntilExpiry = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        combined.push({
          id: q.id,
          type: "quote",
          sequence: q.sequence,
          projectName: q.projectName || q.quoteRequest?.projectName,
          status: q.status,
          expiresAt: q.expiresAt,
          daysUntilExpiry,
          clientName: q.client?.name || q.primaryClient?.name,
          total: q.total,
        });
      }
    }

    if (data.rfqs && Array.isArray(data.rfqs)) {
      for (const r of data.rfqs) {
        const expiresAt = new Date(r.expiresAt);
        const daysUntilExpiry = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        combined.push({
          id: r.id,
          type: "rfq",
          sequence: r.sequence,
          projectName: r.projectName,
          status: r.status,
          expiresAt: r.expiresAt,
          daysUntilExpiry,
          clientName: r.client?.name || r.primaryClient?.name,
        });
      }
    }

    combined.sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry);
    setItems(combined);
    setSelectedIds(new Set());
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Listen for custom event to open the modal (fired by nav badge click)
  useEffect(() => {
    const handler = () => setShowModal(true);
    document.body.addEventListener("openExpiringItemsModal", handler);
    return () => document.body.removeEventListener("openExpiringItemsModal", handler);
  }, []);

  // Auto-open modal once per session when critical items exist (<=3 days)
  useEffect(() => {
    if (loading || items.length === 0) return;
    const hasCritical = items.some((i) => i.daysUntilExpiry <= 3);
    if (!hasCritical) return;
    try {
      const shown = sessionStorage.getItem("expiringItemsModalAutoShown");
      if (!shown) {
        setShowModal(true);
        sessionStorage.setItem("expiringItemsModalAutoShown", "1");
      }
    } catch (e) {
      // sessionStorage may not be available
    }
  }, [loading, items]);

  const handleReview = (item: ExpiringItem) => {
    if (item.type === "quote") {
      navigate("/quotes/" + item.id);
    } else {
      navigate("/quote-requests/" + item.id);
    }
  };

  const toggleItem = (id: string, type: string) => {
    const key = type + ":" + id;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === displayItems.length) {
      setSelectedIds(new Set());
    } else {
      const all = new Set(displayItems.map((i) => i.type + ":" + i.id));
      setSelectedIds(all);
    }
  };

  const handleBatchDecline = async (data: { category: string | null; notes?: string; competitorName?: string }) => {
    setBatchSubmitting(true);
    try {
      const quoteIds: string[] = [];
      const rfqIds: string[] = [];
      for (const key of selectedIds) {
        const [type, id] = key.split(":");
        if (type === "quote") quoteIds.push(id);
        else rfqIds.push(id);
      }
      const res = await batchDecline({ quoteIds, rfqIds, ...data });
      if (res.success !== false) {
        toast.add("Declined " + selectedIds.size + " item" + (selectedIds.size === 1 ? "" : "s"), "var(--theme-ui-colors-success)");
        setShowBatchDecline(false);
        setSelectedIds(new Set());
        fetchData();
      } else {
        toast.add(res.message || "Failed to process batch decline", "var(--theme-ui-colors-danger)");
      }
    } catch (e: any) {
      toast.add("Failed to process batch decline", "var(--theme-ui-colors-danger)");
    }
    setBatchSubmitting(false);
  };

  const handleBatchRegenerate = async () => {
    const quoteIds: string[] = [];
    for (const key of selectedIds) {
      const [type, id] = key.split(":");
      if (type === "quote") quoteIds.push(id);
    }
    if (quoteIds.length === 0) {
      toast.add("Select at least one quote to regenerate", "var(--theme-ui-colors-warning)");
      return;
    }
    setRegenSubmitting(true);
    try {
      const res = await batchRegenerate(quoteIds);
      if (res.success !== false) {
        const count = res.regenerated || quoteIds.length;
        const errCount = res.errors?.length || 0;
        let msg = "Regenerated " + count + " quote" + (count === 1 ? "" : "s") + " at current pricing";
        if (errCount > 0) {
          msg += " (" + errCount + " failed)";
        }
        toast.add(msg, "var(--theme-ui-colors-success)");
        setSelectedIds(new Set());
        fetchData();
      } else {
        toast.add(res.message || "Failed to regenerate quotes", "var(--theme-ui-colors-danger)");
      }
    } catch (e: any) {
      toast.add("Failed to regenerate quotes", "var(--theme-ui-colors-danger)");
    }
    setRegenSubmitting(false);
  };

  const handleModalClose = () => {
    setShowModal(false);
    // Refresh dashboard widget data after modal closes (items may have changed)
    fetchData();
  };

  if (loading) {
    return (
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: "6px",
          border: "1px solid",
          borderColor: themevals.colors.inputOutline,
          boxShadow: "0px 4px 12px 0px rgba(113, 125, 150, 0.05)",
          marginBottom: "24px",
          minHeight: "200px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            padding: "16px 20px",
            borderBottom: "1px solid",
            borderColor: themevals.colors.inputOutline,
            minHeight: "56px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "14px",
              fontWeight: 600,
              color: themevals.colors.black,
            }}
          >
            Expiring Quotes & RFQs
          </Box>
        </Box>
        <Box sx={{ padding: "20px", flex: 1 }}>
          <Box sx={{ backgroundColor: "#F5F5F5", height: "80px", borderRadius: "4px" }} />
        </Box>
      </Box>
    );
  }

  // Don't render if nothing is expiring
  if (items.length === 0) {
    return null;
  }

  const criticalCount = items.filter((i) => getUrgency(i.daysUntilExpiry) === "critical").length;
  const totalValue = items.reduce((sum, i) => sum + (i.total || 0), 0);
  const quoteCount = items.filter((i) => i.type === "quote").length;
  const rfqCount = items.filter((i) => i.type === "rfq").length;
  const displayItems = items.slice(0, MAX_DISPLAY);

  // Determine batch decline type — if mixed, default to "quote" categories (superset)
  const selectedItems = displayItems.filter((i) => selectedIds.has(i.type + ":" + i.id));
  const hasSelectedQuotes = selectedItems.some((i) => i.type === "quote");
  const selectedQuoteCount = Array.from(selectedIds).filter((k) => k.startsWith("quote:")).length;
  const batchDeclineType: "quote" | "rfq" = hasSelectedQuotes ? "quote" : "rfq";

  return (
    <>
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: "6px",
          border: "1px solid",
          borderColor: themevals.colors.inputOutline,
          boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
          marginBottom: "24px",
          display: "flex",
          flexDirection: "column",
          maxHeight: COMPONENT_HEIGHT + "px",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            padding: "16px 20px",
            borderBottom: "1px solid",
            borderColor: themevals.colors.inputOutline,
            flexShrink: 0,
          }}
        >
          <Flex sx={{ alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
            <Flex sx={{ alignItems: "center", gap: "10px" }}>
              <Box
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: themevals.colors.black,
                }}
              >
                Expiring Quotes & RFQs
              </Box>
              {criticalCount > 0 && (
                <Box
                  sx={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "#FFFFFF",
                    backgroundColor: themevals.colors.danger,
                    padding: "2px 8px",
                    borderRadius: "10px",
                  }}
                >
                  {criticalCount} urgent
                </Box>
              )}
            </Flex>
            <Flex sx={{ gap: "8px", alignItems: "center" }}>
              {selectedQuoteCount > 0 && (
                <BasicButton
                  scheme="success"
                  onClick={handleBatchRegenerate}
                  $submitting={regenSubmitting}
                  $iconHeight="14px"
                  $iconWidth="14px"
                  $loadingColor="#fff"
                  keepText={true}
                  styles={{ fontSize: "12px", padding: "4px 12px" }}
                >
                  Regenerate ({selectedQuoteCount})
                </BasicButton>
              )}
              {selectedIds.size > 0 && (
                <BasicButton
                  scheme="danger"
                  outline={true}
                  onClick={() => setShowBatchDecline(true)}
                  styles={{ fontSize: "12px", padding: "4px 12px" }}
                >
                  Decline ({selectedIds.size})
                </BasicButton>
              )}
              <BasicButton
                scheme="secondary"
                outline={true}
                onClick={() => setShowModal(true)}
              >
                View All
              </BasicButton>
            </Flex>
          </Flex>
          {/* Summary stats */}
          <Flex sx={{ gap: "16px", alignItems: "center" }}>
            <Box sx={{ fontFamily: "Poppins, sans-serif", fontSize: "12px", color: themevals.colors.dkgrey }}>
              {quoteCount} quote{quoteCount !== 1 ? "s" : ""}
              {rfqCount > 0 ? ", " + rfqCount + " RFQ" + (rfqCount !== 1 ? "s" : "") : ""}
            </Box>
            {totalValue > 0 && (
              <Box sx={{ fontFamily: "Poppins, sans-serif", fontSize: "12px", fontWeight: 600, color: themevals.colors.dkgrey }}>
                {formatCurrency(totalValue)} total pipeline value
              </Box>
            )}
          </Flex>
        </Box>

        {/* Select all bar */}
        {displayItems.length > 1 && (
          <Flex
            sx={{
              padding: "8px 20px",
              borderBottom: "1px solid",
              borderColor: themevals.colors.inputOutline,
              alignItems: "center",
              gap: "8px",
              flexShrink: 0,
            }}
          >
            <Label sx={{ display: "flex", alignItems: "center", cursor: "pointer", margin: 0, padding: 0, minWidth: "18px" }}>
              <Checkbox
                checked={selectedIds.size === displayItems.length && displayItems.length > 0}
                onChange={toggleAll}
                sx={{ cursor: "pointer" }}
              />
            </Label>
            <Box sx={{ fontFamily: "Poppins, sans-serif", fontSize: "12px", color: themevals.colors.dkgrey, cursor: "pointer" }} onClick={toggleAll}>
              {selectedIds.size === displayItems.length ? "Deselect all" : "Select all"}
            </Box>
          </Flex>
        )}

        {/* Content */}
        <Box
          sx={{
            padding: "16px 20px",
            flex: 1,
            overflowY: "auto",
            minHeight: 0,
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          {displayItems.map((item) => (
            <ExpiringItemRow
              key={item.type + "-" + item.id}
              item={item}
              onReview={handleReview}
              selected={selectedIds.has(item.type + ":" + item.id)}
              onToggle={toggleItem}
            />
          ))}
        </Box>

        {/* Batch decline modal */}
        {showBatchDecline && (
          <DeclineReasonModal
            type={batchDeclineType}
            title={"Decline " + selectedIds.size + " Item" + (selectedIds.size === 1 ? "" : "s")}
            onConfirm={handleBatchDecline}
            onCancel={() => setShowBatchDecline(false)}
            submitting={batchSubmitting}
          />
        )}
      </Box>

      {/* Full expiring items modal */}
      <ExpiringItemsModal isOpen={showModal} onClose={handleModalClose} />
    </>
  );
};

export default ExpiringItems;
